import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import ReactMarkdown from "react-markdown";

import CostEstimatorConfig from "./CostEstimator.config";
import CostEstimatorSchema from "./CostEstimator.schema";
import { getProjectCostEstimatorContent } from "./projectCostEstimatorContent";
import ProjectCostEstimatorLeadForm from "./ProjectCostEstimatorLeadForm";
import { useBrand } from "../../../branding/BrandContext";
import { getProjectsForBrand } from "../../../config/projects";

import "../../../assets/stylesheets/toolBase.css";

const importMetaEnv = import.meta.env || {};
const CONFIGURED_API_BASE_URL =
  importMetaEnv.VITE_API_BASE_URL ||
  importMetaEnv.VITE_BASE_API_URL ||
  "";

function isLocalDevelopmentHost(hostname) {
  if (!hostname) {
    return false;
  }

  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.endsWith(".local") ||
    /^10\./.test(hostname) ||
    /^192\.168\./.test(hostname) ||
    /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname)
  );
}

function getApiBaseUrl() {
  if (
    typeof window !== "undefined" &&
    isLocalDevelopmentHost(window.location.hostname)
  ) {
    return "";
  }

  return CONFIGURED_API_BASE_URL || "https://api.home-helper.com";
}

const API_BASE_URL = getApiBaseUrl();

function buildApiUrl(path) {
  return `${API_BASE_URL}${path}`;
}

function normalizeAnswerSnapshot(questions, responses) {
  return questions.map((question) => {
    const selectedValue = responses[question.id] || "";
    const selectedOption = question.options.find(
      (option) => option.value === selectedValue
    );

    return {
      id: question.id,
      question: question.label,
      answer: selectedOption?.label || selectedValue || null,
      answerLabel: selectedOption?.label || null,
      answerValue: selectedOption?.value || selectedValue || null,
      dimension: question.dimension || null,
    };
  });
}

function normalizeQuestionSnapshot(questions) {
  return questions.map((question) => ({
    id: question.id,
    label: question.label,
    helperText: question.helperText || null,
    inputType: question.inputType || "singleChoice",
    dimension: question.dimension || null,
    options: Array.isArray(question.options)
      ? question.options.map((option) => ({
          value: option.value,
          label: option.label,
          description: option.description || null,
        }))
      : [],
  }));
}

const CostEstimatorTool = ({
  zip,
  initialProjectType,
  precomputedEstimate,
  customerId,
  surveyId,
  categoryKey,
}) => {
  const brand = useBrand();
  const { defaults, resultSections } = CostEstimatorConfig;
  const fields = CostEstimatorSchema.fields;

  const hasLegacyReportContext = Boolean(
    precomputedEstimate || (customerId && surveyId && categoryKey)
  );

  const [step, setStep] = useState(hasLegacyReportContext ? 3 : 1);
  const [isStreaming, setIsStreaming] = useState(false);
  const [legacyStreamedText, setLegacyStreamedText] = useState(
    precomputedEstimate || ""
  );
  const [formData, setFormData] = useState({
    projectType: initialProjectType || categoryKey || "",
    zip: zip || defaults.zip,
  });
  const [responses, setResponses] = useState({});
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [generatedEstimate, setGeneratedEstimate] = useState(null);
  const [showLeadForm, setShowLeadForm] = useState(false);

  const hasStreamed = useRef(false);

  const resolvedCategoryKey =
    formData.projectType || categoryKey || initialProjectType || "";

  const estimatorContent = useMemo(
    () => getProjectCostEstimatorContent(resolvedCategoryKey),
    [resolvedCategoryKey]
  );

  const questions = estimatorContent.questions || [];
  const projectTypeOptions = useMemo(
    () => getProjectsForBrand(brand?.brandKey),
    [brand?.brandKey]
  );

  useEffect(() => {
    if (precomputedEstimate) {
      setStep(3);
      setLegacyStreamedText(precomputedEstimate);
      return;
    }

    if (customerId && categoryKey && !hasStreamed.current) {
      hasStreamed.current = true;
      streamFromReport();
    }
  }, [precomputedEstimate, customerId, categoryKey]);

  useEffect(() => {
    if (hasLegacyReportContext) {
      return;
    }

    setResponses({});
    setGeneratedEstimate(null);
    setShowLeadForm(false);
    setApiError("");
  }, [resolvedCategoryKey, hasLegacyReportContext]);

  const streamFromReport = async () => {
    setStep(3);
    setIsStreaming(true);
    setLegacyStreamedText("");
    setApiError("");

    try {
      const response = await fetch(
        buildApiUrl("/api/cost-estimate/from-report"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream",
          },
          body: JSON.stringify({
            customerId,
            surveyId,
            categoryKey,
          }),
        }
      );

      if (!response.ok || !response.body) {
        const data = await response.json().catch(() => ({}));
        setApiError(
          data.error || "Unable to generate a cost estimate right now."
        );
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      let accumulated = "";
      let previousChunk = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const raw = decoder.decode(value, { stream: true });
        const cleaned = raw
          .replace(/^data:\s?/gm, "")
          .replace(/\[DONE\]/g, "");

        if (!cleaned) continue;

        if (
          previousChunk &&
          !previousChunk.endsWith(" ") &&
          !previousChunk.endsWith("\n") &&
          !cleaned.startsWith(" ") &&
          !cleaned.startsWith(",") &&
          !cleaned.startsWith(".") &&
          !cleaned.startsWith("–") &&
          !cleaned.startsWith(":")
        ) {
          accumulated += " ";
        }

        accumulated += cleaned;
        previousChunk = cleaned;
        setLegacyStreamedText(accumulated);
      }
    } catch (error) {
      console.error("Cost estimate stream failed:", error);
      setApiError(
        "Unable to reach the estimate service right now. Please try again in a moment."
      );
    } finally {
      setIsStreaming(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleQuestionAnswer = (questionId, value) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const validateStepOne = () => {
    const newErrors = {};

    if (!formData.projectType && !resolvedCategoryKey) {
      newErrors.projectType = "Please select a project category.";
    }

    if (!formData.zip) {
      newErrors.zip = "ZIP Code is required.";
    } else if (!fields.zip.validation.pattern.test(formData.zip)) {
      newErrors.zip = fields.zip.validation.message;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateEstimatorQuestions = () => {
    if (!Array.isArray(questions) || questions.length !== 4) {
      setApiError("This category does not have four estimator questions available yet.");
      return false;
    }

    const unanswered = questions.filter((question) => !responses[question.id]);
    if (unanswered.length > 0) {
      setApiError("Please answer all four questions before generating your estimate.");
      return false;
    }

    if (!formData.zip || !fields.zip.validation.pattern.test(formData.zip)) {
      setApiError(fields.zip.validation.message);
      return false;
    }

    setApiError("");
    return true;
  };

  const generateEstimator = async () => {
    setStep(3);
    setIsStreaming(true);
    setGeneratedEstimate(null);
    setShowLeadForm(false);
    setApiError("");

    const questionSnapshot = normalizeQuestionSnapshot(questions);
    const answerSnapshot = normalizeAnswerSnapshot(questions, responses);

    try {
      const response = await fetch(buildApiUrl("/api/tools/project-cost-estimator"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          categoryKey: resolvedCategoryKey,
          categorySlug: resolvedCategoryKey,
          categoryLabel: estimatorContent.categoryLabel,
          zip: formData.zip,
          pageUrl:
            typeof window !== "undefined" && window.location?.href
              ? window.location.href
              : "",
          source: "project-cost-estimator",
          questions: questionSnapshot,
          answers: answerSnapshot,
          customerId: customerId || null,
          surveyId: surveyId || null,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        throw new Error(data.error || "Unable to generate a cost estimate right now.");
      }

      setGeneratedEstimate(data);
      setShowLeadForm(false);
    } catch (error) {
      console.error("Project cost estimator error:", error);
      setApiError(
        error.message ||
          "We couldn’t generate a project cost estimate right now. Please try again in a moment."
      );
      setStep(2);
    } finally {
      setIsStreaming(false);
    }
  };

  const leadCtaLabel = estimatorContent.categoryLabel
    ? `Get matched with local ${estimatorContent.categoryLabel} pros`
    : "Get matched with local pros";
  const formTitle = estimatorContent.categoryLabel
    ? `Tell us where you need ${estimatorContent.categoryLabel} help`
    : "Tell us where you need help";
  const formDescription = estimatorContent.categoryLabel
    ? `Share your contact details so local ${estimatorContent.categoryLabel} pros can review your estimate.`
    : "Share your contact details so local pros can review your estimate.";
  const successMessage = estimatorContent.categoryLabel
    ? `Thanks — your ${estimatorContent.categoryLabel} request has been saved.`
    : "Thanks — your project request has been saved.";

  const pageUrl =
    typeof window !== "undefined" && window.location?.href ? window.location.href : "";

  const estimatorLeadContext = generatedEstimate
    ? {
        reportId: generatedEstimate.reportId,
        categoryKey: generatedEstimate.categoryKey || resolvedCategoryKey,
        categoryLabel:
          generatedEstimate.categoryLabel || estimatorContent.categoryLabel,
        summary: generatedEstimate.summary,
        pageUrl,
      }
    : {
        reportId: "",
        categoryKey: resolvedCategoryKey,
        categoryLabel: estimatorContent.categoryLabel,
        summary: "",
        pageUrl,
      };

  const renderLegacyResult = () => (
    <section className="tool-results estimate-layout">
      <div className="estimate-avatar">
        <img src="../../assets/tom.png" alt={`Tom from ${brand.brandName}™`} />
      </div>

      <div className="estimate-stream">
        <div className="estimate-content">
          <ReactMarkdown>{legacyStreamedText}</ReactMarkdown>
          {isStreaming && <span className="caret">▌</span>}
          {apiError && <p className="tool-field-error">{apiError}</p>}
        </div>

        {customerId ? (
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              marginBottom: "16px",
            }}
          >
            <a
              href={`/report/${customerId}`}
              className="tool-button-primary"
              style={{
                textDecoration: "none",
                padding: "10px 18px",
                fontSize: "14px",
              }}
            >
              View Full Report
            </a>
          </div>
        ) : null}
      </div>
    </section>
  );

  const renderEstimatorResult = () => {
    if (!generatedEstimate) {
      return null;
    }

    return (
      <section className="tool-results">
        <div className="tool-result-card">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 16,
              marginBottom: 8,
            }}
          >
            <strong>{resultSections.estimate.title}</strong>

            <div
              style={{
                marginLeft: "auto",
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid rgba(0,0,0,0.06)",
                backgroundColor: "#EEF2FF",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                minWidth: 170,
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  color: "#4338CA",
                }}
              >
                Estimated Range
              </span>
              <span
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#1F2937",
                  marginTop: 2,
                }}
              >
                {generatedEstimate.estimateRange?.label || "$?-$?"}
              </span>
            </div>
          </div>

          <p className="tool-muted" style={{ whiteSpace: "pre-line" }}>
            {generatedEstimate.summary}
          </p>

          <div style={{ marginTop: 16 }}>
            <ReactMarkdown>{generatedEstimate.markdown}</ReactMarkdown>
          </div>

          {generatedEstimate && !showLeadForm ? (
            <div style={{ marginTop: 18 }}>
              <button
                type="button"
                className="tool-button-primary"
                onClick={() => setShowLeadForm(true)}
              >
                {leadCtaLabel}
              </button>
            </div>
          ) : null}

          {showLeadForm ? (
            <ProjectCostEstimatorLeadForm
              ctaLabel="Send this estimate to local pros"
              formTitle={formTitle}
              formDescription={formDescription}
              successMessage={successMessage}
              initialZipCode={formData.zip}
              estimatorContext={estimatorLeadContext}
            />
          ) : null}
        </div>
      </section>
    );
  };

  return (
    <div className="tool cost-estimator-tool">
      <header className="tool-header">
        <h1 className="tool-title">{CostEstimatorConfig.title}</h1>
        <p className="tool-description">
          {step === 3 && (hasLegacyReportContext || generatedEstimate)
            ? "Here’s a great starting point for your project."
            : CostEstimatorConfig.description}
        </p>
      </header>

      {step === 1 && !hasLegacyReportContext && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (validateStepOne()) setStep(2);
          }}
        >
          <div className="tool-form-row">
            <label className="tool-label">Project Category</label>
            <select
              name="projectType"
              className="tool-select"
              value={formData.projectType}
              onChange={handleChange}
            >
              <option value="">Select a project</option>
              {projectTypeOptions.map((project) => (
                <option key={project.slug} value={project.slug}>
                  {project.label}
                </option>
              ))}
            </select>
            {errors.projectType && (
              <span className="tool-muted">{errors.projectType}</span>
            )}
          </div>

          <div className="tool-form-row">
            <label className="tool-label">{fields.zip.label}</label>
            <input
              name="zip"
              className="tool-input"
              value={formData.zip}
              onChange={handleChange}
              placeholder={fields.zip.placeholder}
            />
            {errors.zip && <span className="tool-muted">{errors.zip}</span>}
          </div>

          <div className="tool-actions">
            <button className="tool-button-primary">Continue</button>
          </div>
        </form>
      )}

      {step === 2 && !hasLegacyReportContext && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (validateEstimatorQuestions()) {
              generateEstimator();
            }
          }}
        >
          <div className="tool-section">
            <strong>{estimatorContent.heading}</strong>
            <p className="tool-muted" style={{ marginTop: 8 }}>
              {estimatorContent.description}
            </p>

            {questions.map((question) => (
              <div className="tool-form-row" key={question.id}>
                <label className="tool-label" htmlFor={question.id}>
                  {question.label}
                </label>
                {question.helperText ? (
                  <p className="tool-muted" style={{ marginTop: 0, marginBottom: 6 }}>
                    {question.helperText}
                  </p>
                ) : null}
                <select
                  id={question.id}
                  className="tool-select"
                  value={responses[question.id] || ""}
                  onChange={(e) => handleQuestionAnswer(question.id, e.target.value)}
                >
                  <option value="">Select an answer</option>
                  {question.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <div className="tool-actions">
            <button
              type="button"
              className="tool-button-secondary"
              onClick={() => setStep(1)}
            >
              Back
            </button>
            <button className="tool-button-primary" disabled={isStreaming}>
              {isStreaming ? "Generating estimate..." : CostEstimatorConfig.ctaLabel}
            </button>
          </div>
        </form>
      )}

      {step === 3 && (hasLegacyReportContext ? renderLegacyResult() : renderEstimatorResult())}

      {step !== 3 && apiError && <p className="tool-field-error">{apiError}</p>}

      {hasLegacyReportContext && step === 3 && apiError && (
        <p className="tool-field-error">{apiError}</p>
      )}
    </div>
  );
};

CostEstimatorTool.propTypes = {
  zip: PropTypes.string,
  initialProjectType: PropTypes.string,
  precomputedEstimate: PropTypes.string,
  customerId: PropTypes.string,
  surveyId: PropTypes.string,
  categoryKey: PropTypes.string,
};

export default CostEstimatorTool;
